import React, { useEffect, useState } from 'react';

import Portal from 'common/common/Portal';
import delayer from 'common/util/delayer';

import type { Option } from 'common/ui/common/select/SelectCommon';
import type { UseComboboxPropGetters, UseSelectPropGetters } from 'downshift';

interface SelectPortalProps {
  align?: 'start' | 'center' | 'end';
  children: React.ReactNode;
  className?: string;
  getMenuProps:
    | UseComboboxPropGetters<Option>['getMenuProps']
    | UseSelectPropGetters<Option>['getMenuProps'];
  optionsMaxWidth?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  triggerRef: React.RefObject<HTMLDivElement>;
}

const SelectPortal = ({
  align = 'start',
  children,
  getMenuProps,
  optionsMaxWidth,
  position = 'bottom',
  triggerRef,
}: SelectPortalProps) => {
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    const updateTriggerWidth = () => {
      if (triggerRef.current) {
        setTriggerWidth(triggerRef.current.offsetWidth);
      }
    };

    updateTriggerWidth();

    const debouncedResizeHandler = new delayer(updateTriggerWidth, 100);
    const handleResize = () => debouncedResizeHandler.callAfterDelay();

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      debouncedResizeHandler.cancel();
    };
  }, [triggerRef]);

  const dropdownWidth = optionsMaxWidth || (triggerWidth ? `${triggerWidth}px` : '100%');

  // Suppresses super annoying unnecessary ref errors from downshift because we're rendering options in a portal
  const hiddenMenuProps = getMenuProps({}, { suppressRefError: true });

  return (
    <Portal align={align} position={position} relativeToRef={triggerRef}>
      <ul style={{ display: 'none' }} {...hiddenMenuProps} />
      <div style={{ width: dropdownWidth }}>{children}</div>
    </Portal>
  );
};

export default SelectPortal;
